/**
 * StatsLegend Component
 * Displays the ranking categories legend and scoring guide for Stream Monitor
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';

const StatsLegend = () => {
  const [showGuide, setShowGuide] = useState(false);
  
  return (
    <>
      {/* Schedule Legend - Αυτόματη Λειτουργία */}
      <div className="text-sm font-medium flex flex-wrap gap-x-6 gap-y-2 mb-3 py-3 px-4 bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700">
        <span className="font-bold text-gray-700 dark:text-gray-300">Αυτόματη Λειτουργία:</span>
        <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
          <span className="w-3 h-3 rounded-full bg-green-500 shadow-lg shadow-green-500/50"></span> 
          <span>Ενεργό</span>
        </span>
        <span className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
          <span className="w-3 h-3 rounded-full bg-yellow-500 shadow-lg shadow-yellow-500/50"></span> 
          <span>Χειροκίνητο override</span>
        </span>
        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
          <span className="w-3 h-3 rounded-full bg-gray-500 shadow-lg shadow-gray-500/30"></span> 
          <span>Εκτός προγράμματος</span>
        </span>
        <span className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
          <span>🕐</span> 
          <span>Χρονοπρογραμματισμένο</span>
        </span>
        <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
          <span>⚡</span> 
          <span>Override ενεργό</span>
        </span>
      </div>
      
      {/* Ranking Categories Legend */}
      <div className="text-sm font-medium flex flex-wrap gap-x-4 gap-y-2 mb-4 py-3 px-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700">
        {/* Current Hits (≤2 years) */}
        <span className="font-bold text-gray-700 dark:text-gray-300">Τρέχουσες (≤2 έτη):</span>
        <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
          <span>🔥</span> 
          <span>POWER <span className="text-xs text-gray-500">(8%)</span></span>
        </span>
        <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
          <span>⭐</span> 
          <span>SECONDARY <span className="text-xs text-gray-500">(15%)</span></span>
        </span>
        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
          <span>✅</span> 
          <span>CURRENTS <span className="text-xs text-gray-500">(υπόλοιπα)</span></span>
        </span>
        
        {/* Older songs */}
        <span className="ml-1 pl-2 border-l border-gray-300 dark:border-gray-600 font-bold text-gray-700 dark:text-gray-300">Παλαιότερες:</span>
        <span className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400">
          <span>🔄</span> 
          <span>RECURRENTS <span className="text-xs text-gray-500">(3-5 έτη)</span></span>
        </span>
        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
          <span>📚</span> 
          <span>GOLD <span className="text-xs text-gray-500">(6+ έτη)</span></span>
        </span>
        
        {/* Low performance */}
        <span className="ml-1 pl-2 border-l border-gray-300 dark:border-gray-600 font-bold text-gray-700 dark:text-gray-300">Χαμηλή:</span>
        <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-500">
          <span>⚠️</span> 
          <span>SLD <span className="text-xs text-gray-500">(ret 40-59%)</span></span>
        </span>
        <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
          <span>❌</span> 
        {/* CUT category */}
        <span>CUT <span className="text-xs text-gray-500">(ret &lt;40%)</span></span>
        </span>
        
        {/* 2026-05-15: NEW/ADD legend hidden by user request — duplicate
            canonicals were producing false NEW labels for songs in rotation. */}
        
        {/* Guide toggle button */}
        <button 
          onClick={() => setShowGuide(!showGuide)}
          className="ml-auto flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
        >
          <Info className="w-4 h-4" />
          <span>Οδηγός</span>
          {showGuide ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      
      {/* Detailed Guide - Expandable */}
      {showGuide && (
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 text-sm">
          <h4 className="font-bold text-blue-800 dark:text-blue-300 mb-3">📊 Πλήρης Οδηγός Stream Monitor</h4>
          
          {/* Metrics Definitions with Examples */}
          <div className="mb-4">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">📈 Επεξήγηση Μετρήσεων:</h5>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-100 dark:bg-gray-800">
                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">Μετρική</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-left">Τι Μετράει</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center">🟢 Καλό</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center">🟡 Μέτριο</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center">🔴 Κακό</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 dark:text-gray-400">
                  <tr>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 font-medium">Retention %</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">Πόσοι ακροατές παρέμειναν μέχρι το ΤΕΛΟΣ</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-green-600">&gt;90%</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-yellow-600">70-90%</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-red-600">&lt;70%</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 font-medium">Early Drop %</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">Πόσοι ΕΦΥΓΑΝ στα πρώτα 30&quot; / intro (αδύναμο intro;)</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-green-600">&lt;5%</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-yellow-600">5-10%</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-red-600">&gt;10%</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 font-medium">LSI</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">Πόσο ΣΤΑΘΕΡΟ ήταν το κοινό (χωρίς tune-in/out)</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-green-600">≥0.95 💎</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-yellow-600">0.85-0.95 ✨</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-red-600">&lt;0.85 ⚡</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 font-medium">Adjusted Score</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">ΤΕΛΙΚΟ ΣΚΟΡ (Retention × EarlyDrop × LSI - Penalty)</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-green-600">&gt;85% POWER</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-yellow-600">70-85%</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-red-600">&lt;70% CUT</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 font-medium">TLH</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">Συνολικός χρόνος ακρόασης σε ΩΡΕΣ</td>
                    <td colSpan="3" className="border border-gray-300 dark:border-gray-600 px-2 py-1 text-center text-gray-500">Όσο μεγαλύτερο, τόσο καλύτερο</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* LSI Badge Legend */}
          <div className="mb-4 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">LSI Badges στον πίνακα:</h5>
            <div className="flex flex-wrap gap-4 text-xs">
              <span className="text-blue-500">💎 ≥1.0 = Τέλειο</span>
              <span className="text-green-500">✨ 0.9-1.0 = Καλό</span>
              <span className="text-yellow-500">• 0.8-0.9 = Μέτριο</span>
              <span className="text-orange-400">⚡ &lt;0.8 = Ασταθές</span>
              <span className="text-gray-400">— = Ανεπαρκή δεδομένα (&lt;90 δευτ.)</span>
            </div>
          </div>
          
          {/* Retention Example */}
          <div className="mb-4 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">🎵 Παράδειγμα Retention (τραγούδι 3 λεπτών):</h5>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse font-mono">
                <thead>
                  <tr className="bg-gray-100 dark:bg-gray-700">
                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1">Poll</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1">Χρόνος</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-2 py-1">Ακροατές</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 dark:text-gray-400">
                  <tr className="bg-green-50 dark:bg-green-900/20">
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">1</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">0:00</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 font-bold text-green-600">120 (start)</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">2</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">0:30</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">118</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">3</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">1:00</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">115 <span className="text-orange-500">(Early Drop: 4.2%)</span></td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">4-6</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">1:30-2:30</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">112→108</td>
                  </tr>
                  <tr className="bg-blue-50 dark:bg-blue-900/20">
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">7</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1">3:00</td>
                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-1 font-bold text-blue-600">105 (end)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              <strong>Retention:</strong> 105 / 120 × 100 = <span className="text-green-600 font-bold">87.5%</span> 🟢
              <span className="ml-2">| <strong>Early Drop:</strong> (120-115)/120 = <span className="text-green-600 font-bold">4.2%</span> 🟢</span>
            </p>
          </div>
          
          {/* Ranking Logic */}
          <div className="mb-4">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">🏆 Λογική Ranking (μόνο για &quot;Χθες&quot;):</h5>
            <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
              <div className="flex items-start gap-2">
                <span className="text-red-500">1.</span>
                <span><strong>Retention Check:</strong> Αν &lt;40% → CUT ❌, αν 40-59% → SLOW DOWN ⚠️</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-orange-500">2.</span>
                <span><strong>NEW/ADD Check:</strong> Αν προστέθηκε πρόσφατα στο rotation</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-green-500">3.</span>
                <span><strong>Age-Based Pools:</strong></span>
              </div>
              <div className="ml-6 space-y-1">
                <div>• ≤2 χρόνια → 🔥 POWER (8%) / ⭐ SECONDARY (15%) / ✅ CURRENTS</div>
                <div>• 3-5 χρόνια → 🔄 RECURRENTS</div>
                <div>• 6+ χρόνια → 📚 GOLD</div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-blue-500">4.</span>
                <span><strong>Adjusted Score:</strong> Τελική ταξινόμηση = Retention × Early Drop Mult × LSI Factor - Penalty</span>
              </div>
            </div>
          </div>
          
          {/* Penalty Table */}
          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
            <h5 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2">⚠️ Penalty για Λίγα Plays:</h5>
            <div className="flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-400">
              <span>1 play: <span className="text-red-500 font-bold">-25%</span></span>
              <span>2 plays: <span className="text-orange-500 font-bold">-15%</span></span>
              <span>3 plays: <span className="text-yellow-600 font-bold">-10%</span></span>
              <span>4 plays: <span className="text-yellow-500 font-bold">-5%</span></span>
              <span>5+ plays: <span className="text-green-500 font-bold">0%</span></span>
            </div>
            <p className="mt-1 text-xs text-gray-500">* Μειωμένο penalty αν το τραγούδι έχει quality signal (Ret &gt;90% ΚΑΙ Early Drop &lt;3%)</p>
          </div>
          
          {/* NEW/ADD Logic */}
          <div className="mb-4">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">🆕 NEW vs ➕ ADD:</h5>
            <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
              <div><span className="text-green-600 font-bold">🆕 NEW:</span> Πρώτη μετάδοση ≤1 ημέρα + Κυκλοφορία τρέχον έτος ή ≤2 μήνες πριν</div>
              <div><span className="text-cyan-600 font-bold">➕ ADD:</span> Πρώτη μετάδοση ≤1 ημέρα + Κυκλοφορία ≥4 μήνες πριν ή παλαιότερο έτος</div>
            </div>
          </div>
          
          {/* Periods */}
          <div className="mb-4">
            <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">📅 Περίοδοι:</h5>
            <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
              <div><strong>Σήμερα (Preview):</strong> Ημιτελή δεδομένα, χωρίς ranking</div>
              <div><strong>Χθες:</strong> Πλήρες ranking με όλες τις κατηγορίες</div>
              <div><strong>Τρέχουσα Εβδομάδα:</strong> Preview από Δευτέρα μέχρι σήμερα</div>
              <div><strong>Άλλες περίοδοι:</strong> Συγκεντρωτικά, χωρίς ranking</div>
            </div>
          </div>
          
          {/* Early Drop Multipliers - Scoring Settings Explanation */}
          <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded border border-purple-200 dark:border-purple-800">
            <h5 className="font-semibold text-purple-800 dark:text-purple-300 mb-2">⚙️ Early Drop Multipliers (Πολλαπλασιαστές Πρώιμης Φυγής)</h5>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
              Πόσο &quot;τιμωρείται&quot; το score όταν οι ακροατές φεύγουν στα πρώτα 30&quot; (intro) του τραγουδιού:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-purple-100 dark:bg-purple-900/30">
                    <th className="border border-purple-300 dark:border-purple-700 px-2 py-1">Κατηγορία</th>
                    <th className="border border-purple-300 dark:border-purple-700 px-2 py-1">Early Drop %</th>
                    <th className="border border-purple-300 dark:border-purple-700 px-2 py-1">Multiplier</th>
                    <th className="border border-purple-300 dark:border-purple-700 px-2 py-1">Επίδραση</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 dark:text-gray-400">
                  <tr className="bg-green-50 dark:bg-green-900/10">
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1 font-medium text-green-600">🟢 Excellent</td>
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1 text-center">≤2%</td>
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1 text-center font-bold">×1.00</td>
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1">Καμία ποινή</td>
                  </tr>
                  <tr className="bg-blue-50 dark:bg-blue-900/10">
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1 font-medium text-blue-600">🔵 Good</td>
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1 text-center">2-5%</td>
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1 text-center font-bold">×0.95</td>
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1">-5% στο score</td>
                  </tr>
                  <tr className="bg-yellow-50 dark:bg-yellow-900/10">
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1 font-medium text-yellow-600">🟡 Moderate</td>
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1 text-center">5-10%</td>
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1 text-center font-bold">×0.85</td>
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1">-15% στο score</td>
                  </tr>
                  <tr className="bg-red-50 dark:bg-red-900/10">
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1 font-medium text-red-600">🔴 Poor</td>
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1 text-center">&gt;10%</td>
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1 text-center font-bold">×0.70</td>
                    <td className="border border-purple-300 dark:border-purple-700 px-2 py-1">-30% στο score</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-500 italic">
              💡 <strong>Παράδειγμα:</strong> Τραγούδι με Retention 90% και Early Drop 8% (Moderate) → Τελικό Score: 90 × 0.85 = 76.5
            </p>
          </div>
          
          {/* Quality Signal Explanation */}
          <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded border border-emerald-200 dark:border-emerald-800">
            <h5 className="font-semibold text-emerald-800 dark:text-emerald-300 mb-2">💎 Quality Signal (Σήμα Ποιότητας)</h5>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
              Τα τραγούδια με &quot;Quality Signal&quot; απολαμβάνουν <strong>μειωμένο penalty</strong> για λίγα plays, 
              γιατί η εξαιρετική συμπεριφορά των ακροατών δείχνει ότι το τραγούδι αξίζει παρά τη μικρή δειγματοληψία.
            </p>
            <div className="flex flex-wrap gap-4 text-xs mb-2">
              <div className="p-2 bg-white dark:bg-gray-800 rounded border">
                <span className="font-bold text-emerald-600">Retention Threshold:</span>
                <span className="ml-1">&gt;90%</span>
              </div>
              <div className="p-2 bg-white dark:bg-gray-800 rounded border">
                <span className="font-bold text-emerald-600">Early Drop Threshold:</span>
                <span className="ml-1">&lt;3%</span>
              </div>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
              <strong>Συνθήκη:</strong> Για να έχει Quality Signal, ένα τραγούδι πρέπει να έχει ΚΑΙ Retention &gt;90% ΚΑΙ Early Drop &lt;3%
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-emerald-100 dark:bg-emerald-900/30">
                    <th className="border border-emerald-300 dark:border-emerald-700 px-2 py-1">Plays</th>
                    <th className="border border-emerald-300 dark:border-emerald-700 px-2 py-1">Κανονικό Penalty</th>
                    <th className="border border-emerald-300 dark:border-emerald-700 px-2 py-1">Με Quality Signal</th>
                    <th className="border border-emerald-300 dark:border-emerald-700 px-2 py-1">Όφελος</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 dark:text-gray-400">
                  <tr>
                    <td className="border border-emerald-300 dark:border-emerald-700 px-2 py-1 text-center font-bold">1</td>
                    <td className="border border-emerald-300 dark:border-emerald-700 px-2 py-1 text-center text-red-500">-25%</td>
                    <td className="border border-emerald-300 dark:border-emerald-700 px-2 py-1 text-center text-green-600">-12.5%</td>
                    <td className="border border-emerald-300 dark:border-emerald-700 px-2 py-1 text-center text-emerald-600">+12.5%</td>
                  </tr>
                  <tr>
                    <td className="border border-emerald-300 dark:border-emerald-700 px-2 py-1 text-center font-bold">2</td>
                    <td className="border border-emerald-300 dark:border-emerald-700 px-2 py-1 text-center text-orange-500">-15%</td>
                    <td className="border border-emerald-300 dark:border-emerald-700 px-2 py-1 text-center text-green-600">-7.5%</td>
                    <td className="border border-emerald-300 dark:border-emerald-700 px-2 py-1 text-center text-emerald-600">+7.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-500 italic">
              💡 <strong>Παράδειγμα:</strong> Νέο τραγούδι με 1 play, Retention 95%, Early Drop 1% → Αντί -25% παίρνει μόνο -12.5% penalty
            </p>
          </div>
          
          {/* Data Quality - Listener Thresholds */}
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800">
            <h5 className="font-semibold text-amber-800 dark:text-amber-300 mb-2">📊 Αξιοπιστία Δεδομένων Ακροατών</h5>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
              Τα δεδομένα ακροατών αξιολογούνται βάσει κατωφλίων ανά σταθμό (percentiles της τελευταίας εβδομάδας):
            </p>
            <div className="overflow-x-auto mb-2">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-amber-100 dark:bg-amber-900/30">
                    <th className="border border-amber-300 dark:border-amber-700 px-2 py-1">Κατηγορία</th>
                    <th className="border border-amber-300 dark:border-amber-700 px-2 py-1">Συνθήκη</th>
                    <th className="border border-amber-300 dark:border-amber-700 px-2 py-1">Βάρος</th>
                    <th className="border border-amber-300 dark:border-amber-700 px-2 py-1">Σημασία</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 dark:text-gray-400">
                  <tr className="bg-green-50 dark:bg-green-900/10">
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 font-medium">🟢 Αξιόπιστο</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 text-center">≥ Υψηλό (75%)</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 text-center font-bold">×1.0</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1">Πλήρης εμπιστοσύνη</td>
                  </tr>
                  <tr className="bg-yellow-50 dark:bg-yellow-900/10">
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 font-medium">🟡 Προσέγγιση</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 text-center">≥ Μέσο (50%)</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 text-center font-bold">×0.9</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1">Καλή εκτίμηση</td>
                  </tr>
                  <tr className="bg-orange-50 dark:bg-orange-900/10">
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 font-medium">🟠 Χαμηλή</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 text-center">≥ Χαμηλό (25%)</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 text-center font-bold">×0.7</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1">Με επιφύλαξη</td>
                  </tr>
                  <tr className="bg-red-50 dark:bg-red-900/10">
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 font-medium">🔴 Αναξιόπιστο</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 text-center">&lt; Χαμηλό</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 text-center font-bold">×0.4</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1">Μη αξιόπιστο</td>
                  </tr>
                  <tr className="bg-gray-50 dark:bg-gray-900/10">
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 font-medium">⚪ Άγνωστο</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 text-center">Χωρίς κατώφλια</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1 text-center font-bold">×0.5</td>
                    <td className="border border-amber-300 dark:border-amber-700 px-2 py-1">Δεν έχουν οριστεί</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <p><strong>Επίδραση:</strong> Με αναξιόπιστα δεδομένα, Early Drop &amp; LSI οδηγούνται προς ουδέτερες τιμές</p>
              <p><strong>Quality Signal:</strong> Δεν ενεργοποιείται αν τα δεδομένα είναι αναξιόπιστα/χαμηλής εμπιστοσύνης</p>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-500 italic">
              💡 Τα κατώφλια υπολογίζονται αυτόματα κάθε Δευτέρα 00:01 ή χειροκίνητα στις ρυθμίσεις σταθμού
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default StatsLegend;
